import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Download, MessageCircle, Eye, Phone, Mail, MapPin, PawPrint, Package, Calendar, Crown, Trash2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

function toCsv(rows, columns) {
  const esc = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const header = columns.map((c) => c.label).join(',');
  const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(',')).join('\n');
  return `${header}\n${body}`;
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function waLink(phone, prefill = '') {
  const digits = String(phone || '').replace(/\D+/g, '');
  if (!digits) return '';
  const q = prefill ? `?text=${encodeURIComponent(prefill)}` : '';
  return `https://wa.me/${digits}${q}`;
}

function PhoneCell({ row }) {
  const phone = row?.phone;
  if (!phone) return <span className="text-[#7A8A99]">—</span>;
  const url = waLink(
    phone,
    `Hi ${row?.name || 'there'}, this is SmartPaw — thanks for signing up! How can we help?`,
  );
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <a
        href={`tel:${phone}`}
        className="text-[#0A4D8C] font-semibold hover:underline"
        data-testid="lead-phone-tel"
      >
        {phone}
      </a>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="lead-whatsapp-link"
          title="Reply on WhatsApp"
          className="inline-flex items-center gap-1 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-[11px] font-bold px-2.5 py-1 transition"
        >
          <MessageCircle size={12} /> WA
        </a>
      )}
    </div>
  );
}

export function AdminLeads() {
  return (
    <SimpleList
      title="Leads"
      eyebrow="Sign-ups"
      testId="admin-leads-page"
      fetcher={() => adminApi.listLeads()}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone', render: (row) => <PhoneCell row={row} /> },
        { key: 'pet_type', label: 'Pet' },
        { key: 'pet_name', label: 'Pet name' },
        { key: 'pet_breed', label: 'Breed' },
        { key: 'created_at', label: 'Created' },
      ]}
      csvName="smartpaw-leads.csv"
    />
  );
}

export function AdminContacts() {
  return (
    <SimpleList
      title="Contact Inquiries"
      eyebrow="Inbox"
      testId="admin-contacts-page"
      fetcher={() => adminApi.listContacts()}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'subject', label: 'Subject' },
        { key: 'message', label: 'Message' },
        { key: 'department', label: 'Dept' },
        { key: 'created_at', label: 'Created' },
      ]}
      csvName="smartpaw-contacts.csv"
    />
  );
}

export function AdminCabinetCustomers() {
  const [detailUser, setDetailUser] = useState(null);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [deletingRow, setDeletingRow] = useState(null); // customer row pending delete confirmation
  const [deleteWorking, setDeleteWorking] = useState(false);

  useEffect(() => {
    if (!detailUser?.id) return;
    let alive = true;
    setDetails(null);
    setDetailsError('');
    setLoadingDetails(true);
    (async () => {
      try {
        const d = await adminApi.getCabinetCustomerDetails(detailUser.id);
        if (alive) setDetails(d);
      } catch (e) {
        if (alive) setDetailsError(e.message || 'Failed to load profile');
      } finally {
        if (alive) setLoadingDetails(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [detailUser?.id]);

  const renderView = (row) => (
    <div className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => setDetailUser(row)}
        data-testid={`view-customer-${row.id}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#0A4D8C] hover:bg-[#053B70] text-white text-[11px] font-bold px-3 py-1.5 transition"
      >
        <Eye size={12} /> View
      </button>
      <button
        type="button"
        onClick={() => setDeletingRow(row)}
        data-testid={`delete-customer-${row.id}`}
        title="Delete customer"
        className="inline-flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 text-[11px] font-bold p-1.5 transition"
      >
        <Trash2 size={12} />
      </button>
    </div>
  );

  const confirmDelete = async () => {
    if (!deletingRow) return;
    setDeleteWorking(true);
    try {
      await adminApi.deleteCabinetCustomer(deletingRow.id);
      toast.success(`Customer ${deletingRow.email || deletingRow.id} deleted`);
      setDeletingRow(null);
      // Force the SimpleList to re-fetch
      setRefreshTrigger((n) => n + 1);
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleteWorking(false);
    }
  };

  const onExportProfile = () => {
    if (!details) return;
    const blob = new Blob([JSON.stringify(details, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeEmail = (details.user?.email || 'customer').replace(/[^a-z0-9]+/gi, '-');
    a.href = url;
    a.download = `smartpaw-customer-${safeEmail}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <SimpleList
        title="Cabinet Customers"
        eyebrow="Registered users"
        testId="admin-cabinet-customers-page"
        fetcher={() => adminApi.listCabinetCustomers()}
        refreshKey={refreshTrigger}
        columns={[
          { key: 'actions', label: '', render: renderView },
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone', render: (row) => <PhoneCell row={row} /> },
          { key: 'provider', label: 'Provider' },
          { key: 'founder_no', label: 'Founder #' },
          { key: 'subscription_count', label: 'Subs' },
          { key: 'pet_count', label: 'Pets' },
          { key: 'address_count', label: 'Addresses' },
          { key: 'created_at', label: 'Joined' },
        ]}
        csvName="smartpaw-cabinet-customers.csv"
      />

      <Sheet
        open={!!detailUser}
        onOpenChange={(o) => {
          if (!o) {
            setDetailUser(null);
            setDetails(null);
            setDetailsError('');
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto bg-white"
          data-testid="customer-detail-drawer"
        >
          <SheetHeader className="mb-5">
            <SheetTitle className="font-display text-2xl text-[#05223D]">
              Customer Profile
            </SheetTitle>
            <SheetDescription className="text-[#465B70]">
              Full registration details, pets, addresses, and subscriptions.
            </SheetDescription>
          </SheetHeader>

          {loadingDetails && (
            <div className="py-12 flex items-center justify-center text-[#465B70]">
              <Loader2 className="w-6 h-6 animate-spin text-[#F25C05]" />
            </div>
          )}

          {detailsError && (
            <div className="mb-5 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">
              {detailsError}
            </div>
          )}

          {details && (
            <div className="space-y-6">
              {/* User */}
              <Section
                icon={<Mail size={16} />}
                title="Account"
                action={
                  <button
                    onClick={onExportProfile}
                    data-testid="export-profile-json"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0A4D8C0F] hover:bg-[#0A4D8C26] text-[#0A4D8C] text-[11px] font-bold px-3 py-1.5 transition"
                  >
                    <Download size={12} /> JSON
                  </button>
                }
              >
                <KV k="Name" v={details.user.name} />
                <KV k="Email" v={details.user.email} />
                <KV
                  k="Phone"
                  v={
                    details.user.phone ? (
                      <a
                        href={`tel:${details.user.phone}`}
                        className="text-[#0A4D8C] font-semibold hover:underline"
                      >
                        {details.user.phone}
                      </a>
                    ) : null
                  }
                />
                <KV k="Provider" v={details.user.provider} />
                <KV
                  k="Founder #"
                  v={
                    details.user.founder_no ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#F25C0526] text-[#F25C05] text-[11px] font-bold px-2 py-0.5">
                        <Crown size={10} /> #{details.user.founder_no}
                        {details.user.founder_status && (
                          <span className="ml-1 opacity-80">· {details.user.founder_status}</span>
                        )}
                      </span>
                    ) : null
                  }
                />
                <KV
                  k="Joined"
                  v={
                    details.user.created_at
                      ? new Date(details.user.created_at).toLocaleString()
                      : null
                  }
                />
              </Section>

              {/* Pets */}
              <Section
                icon={<PawPrint size={16} />}
                title={`Pets (${details.counts.pets})`}
              >
                {details.pets.length === 0 ? (
                  <Empty label="No pets registered." />
                ) : (
                  <div className="space-y-3">
                    {details.pets.map((p) => (
                      <div
                        key={p.id}
                        className="rounded-xl border border-[#0A4D8C1A] bg-[#FDFBF7] p-4"
                      >
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="font-bold text-[#05223D]">
                            {p.name || '—'}{' '}
                            <span className="text-xs font-normal text-[#7A8A99] uppercase">
                              · {p.species || '—'}
                            </span>
                          </div>
                          {p.photo_url && (
                            <img
                              src={p.photo_url}
                              alt={p.name}
                              className="w-12 h-12 rounded-full object-cover border border-[#0A4D8C1A]"
                            />
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <KV k="Breed" v={p.breed} />
                          <KV k="Approx age" v={p.approx_age} />
                          <KV k="Birth date" v={p.birth_date} />
                          <KV k="Weight" v={p.weight_kg ? `${p.weight_kg} kg` : null} />
                        </div>
                        {p.dietary_notes && (
                          <div className="mt-2 text-sm text-[#465B70]">
                            <span className="font-semibold text-[#05223D]">Notes:</span>{' '}
                            {p.dietary_notes}
                          </div>
                        )}
                        {p.quiz && Object.keys(p.quiz).length > 0 && (
                          <details className="mt-2">
                            <summary className="text-xs text-[#0A4D8C] cursor-pointer font-semibold">
                              Quiz answers
                            </summary>
                            <pre className="mt-1 text-[11px] bg-white p-2 rounded border border-[#0A4D8C1A] overflow-x-auto">
                              {JSON.stringify(p.quiz, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Addresses */}
              <Section
                icon={<MapPin size={16} />}
                title={`Addresses (${details.counts.addresses})`}
              >
                {details.addresses.length === 0 ? (
                  <Empty label="No addresses on file." />
                ) : (
                  <div className="space-y-3">
                    {details.addresses.map((a) => (
                      <div
                        key={a.id}
                        className="rounded-xl border border-[#0A4D8C1A] bg-[#FDFBF7] p-4"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-[#05223D]">
                            {a.label || 'Address'}
                          </span>
                          {a.is_default && (
                            <span className="rounded-full bg-[#0A4D8C] text-white text-[10px] font-bold px-2 py-0.5">
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-[#465B70] leading-relaxed">
                          {a.recipient && <div>{a.recipient}</div>}
                          {a.phone && (
                            <div className="text-[#0A4D8C] font-semibold">
                              <Phone size={11} className="inline mr-1" />
                              {a.phone}
                            </div>
                          )}
                          <div>
                            {[a.street, a.building && `bldg ${a.building}`, a.apartment && `apt ${a.apartment}`]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                          <div>
                            {[a.district, a.city, a.postal_code].filter(Boolean).join(', ')}
                          </div>
                          {(a.lat != null && a.lng != null) && (
                            <a
                              href={`https://www.google.com/maps?q=${a.lat},${a.lng}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-[#0A4D8C] hover:underline mt-1"
                            >
                              <MapPin size={11} /> Open in Maps
                            </a>
                          )}
                          {a.notes && (
                            <div className="mt-1 italic text-[#7A8A99]">
                              &ldquo;{a.notes}&rdquo;
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Subscriptions */}
              <Section
                icon={<Calendar size={16} />}
                title={`Subscriptions (${details.counts.subscriptions})`}
              >
                {details.subscriptions.length === 0 ? (
                  <Empty label="No subscriptions yet." />
                ) : (
                  <div className="space-y-3">
                    {details.subscriptions.map((s) => (
                      <div
                        key={s.id}
                        className="rounded-xl border border-[#0A4D8C1A] bg-[#FDFBF7] p-4"
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="font-bold text-[#05223D]">
                            {s.label || s.frequency}{' '}
                            <span className="text-xs font-normal text-[#7A8A99]">
                              · {s.frequency}
                            </span>
                          </div>
                          <span
                            className={`rounded-full text-[10px] font-bold px-2 py-0.5 ${
                              s.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : s.status === 'paused'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {(s.status || 'unknown').toUpperCase()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
                          <KV
                            k="Next delivery"
                            v={
                              s.next_delivery_at
                                ? new Date(s.next_delivery_at).toLocaleString()
                                : null
                            }
                          />
                          <KV k="Payment" v={s.payment_method} />
                          <KV k="Delivers to" v={s.delivery_address_label} />
                          <KV
                            k="Created"
                            v={
                              s.created_at
                                ? new Date(s.created_at).toLocaleDateString()
                                : null
                            }
                          />
                        </div>
                        {s.items_hydrated && s.items_hydrated.length > 0 && (
                          <div className="border-t border-[#0A4D8C1A] pt-2 mt-2">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-[#7A8A99] mb-1">
                              Items
                            </div>
                            <ul className="space-y-1">
                              {s.items_hydrated.map((it, i) => (
                                <li
                                  key={i}
                                  className="flex items-center gap-2 text-sm text-[#05223D]"
                                >
                                  {it.image_url && (
                                    <img
                                      src={it.image_url}
                                      alt=""
                                      className="w-8 h-8 rounded object-cover border border-[#0A4D8C1A]"
                                    />
                                  )}
                                  <span className="flex-1">
                                    {it.brand && (
                                      <span className="text-[#7A8A99] text-xs">
                                        {it.brand} ·{' '}
                                      </span>
                                    )}
                                    {it.name || it.product_id}
                                  </span>
                                  <span className="text-[#465B70] font-semibold">
                                    ×{it.qty}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Orders (recent) */}
              {details.orders && details.orders.length > 0 && (
                <Section
                  icon={<Package size={16} />}
                  title={`Recent orders (${details.counts.orders})`}
                >
                  <div className="space-y-3">
                    {details.orders.slice(0, 10).map((o) => (
                      <div
                        key={o.id}
                        className="rounded-xl border border-[#0A4D8C1A] bg-[#FDFBF7] p-3"
                        data-testid={`order-${o.id}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="text-[#05223D] font-semibold text-sm">
                            #{(o.id || '').slice(0, 8)}
                          </div>
                          <div className="text-xs text-[#465B70]">
                            {o.created_at
                              ? new Date(o.created_at).toLocaleDateString()
                              : '—'}
                          </div>
                          <span
                            className={`rounded-full text-[10px] font-bold px-2 py-0.5 ${
                              o.status === 'delivered'
                                ? 'bg-green-100 text-green-800'
                                : o.status === 'cancelled'
                                ? 'bg-gray-100 text-gray-700'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {(o.status || 'pending').toUpperCase()}
                          </span>
                        </div>
                        {o.delivery_address_label && (
                          <div className="text-[11px] text-[#465B70] mb-1.5">
                            <MapPin size={10} className="inline mr-1" />
                            {o.delivery_address_label}
                          </div>
                        )}
                        {o.items_hydrated && o.items_hydrated.length > 0 ? (
                          <ul className="space-y-1 border-t border-[#0A4D8C14] pt-2">
                            {o.items_hydrated.map((it, i) => (
                              <li
                                key={i}
                                className="flex items-center gap-2 text-xs text-[#05223D]"
                              >
                                {it.image_url && (
                                  <img
                                    src={it.image_url}
                                    alt=""
                                    className="w-7 h-7 rounded object-cover border border-[#0A4D8C1A]"
                                  />
                                )}
                                <span className="flex-1">
                                  {it.brand && (
                                    <span className="text-[#7A8A99]">
                                      {it.brand} ·{' '}
                                    </span>
                                  )}
                                  {it.name || it.product_id || 'Item'}
                                </span>
                                <span className="text-[#465B70] font-semibold">
                                  ×{it.qty}
                                </span>
                                {it.price != null && (
                                  <span className="text-[#0A4D8C] font-bold tabular-nums">
                                    {it.price} ₾
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[11px] italic text-[#7A8A99]">
                            No items recorded.
                          </p>
                        )}
                        {o.total != null && (
                          <div className="mt-2 pt-2 border-t border-[#0A4D8C14] flex justify-end text-sm">
                            <span className="text-[#7A8A99] mr-2">Total:</span>
                            <span className="text-[#05223D] font-bold tabular-nums">
                              {o.total} ₾
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete-customer confirmation */}
      <AlertDialog
        open={!!deletingRow}
        onOpenChange={(o) => !o && !deleteWorking && setDeletingRow(null)}
      >
        <AlertDialogContent
          className="bg-paper"
          data-testid="delete-customer-confirm"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Delete this customer?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{' '}
              <strong className="text-[#05223D]">
                {deletingRow?.email || deletingRow?.name}
              </strong>{' '}
              and ALL of their data: pets, addresses, subscriptions, orders,
              calendar events, surveys, and requests. This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWorking}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteWorking}
              data-testid="delete-customer-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteWorking ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1.5" />
              )}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Section({ icon, title, action, children }) {
  return (
    <section className="card-soft p-4 bg-white" data-testid={`section-${title}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 font-display font-extrabold text-base text-[#05223D]">
          <span className="text-[#F25C05]">{icon}</span> {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function KV({ k, v }) {
  if (v == null || v === '' || v === undefined) {
    return (
      <div className="text-sm">
        <span className="text-[#7A8A99]">{k}: </span>
        <span className="text-[#7A8A99]">—</span>
      </div>
    );
  }
  return (
    <div className="text-sm">
      <span className="text-[#7A8A99]">{k}: </span>
      <span className="text-[#05223D] font-medium">{v}</span>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-[#0A4D8C26] bg-[#FDFBF7] p-4 text-sm text-[#7A8A99] text-center">
      {label}
    </div>
  );
}

function SimpleList({ title, eyebrow, fetcher, columns, csvName, testId, refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetcher();
        if (alive) setItems(data);
      } catch (e) {
        if (alive) setError(e.message);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((row) =>
      columns.some((c) => String(row[c.key] || '').toLowerCase().includes(t)),
    );
  }, [items, q, columns]);

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05]">{eyebrow}</p>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-2">{title}</h1>
          <p className="text-[#465B70] mt-1">
            {items ? `${filtered.length} / ${items.length}` : 'Loading…'} record
            {items?.length === 1 ? '' : 's'}
          </p>
        </div>
        <button
          onClick={() => downloadCsv(csvName, toCsv(filtered, columns))}
          disabled={!items || filtered.length === 0}
          data-testid="export-csv-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#0A4D8C] hover:bg-[#053B70] disabled:opacity-50 text-white text-sm font-bold px-5 py-2.5 transition"
        >
          <Download size={14} /> Export CSV
        </button>
      </div>

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search…"
        className="mb-5 w-full max-w-md rounded-full border border-[#0A4D8C26] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#0A4D8C]"
      />

      {error && (
        <div className="mb-5 rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {items === null ? (
        <div className="py-14 flex items-center justify-center text-[#465B70]">
          <Loader2 className="w-6 h-6 animate-spin text-[#F25C05]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-soft p-10 text-center">
          <p className="font-bold text-[#05223D]">No records.</p>
        </div>
      ) : (
        <div className="card-soft p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F2EB] text-[#465B70] uppercase text-[10px] tracking-wider">
                <tr>
                  {columns.map((c) => (
                    <th key={c.key} className="text-left px-4 py-3 font-bold whitespace-nowrap">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={row.id || i} className="border-t border-[#0A4D8C0F] hover:bg-[#FDFBF7]">
                    {columns.map((c) => (
                      <td key={c.key} className="px-4 py-3 align-top max-w-xs">
                        <div className="text-[#05223D] line-clamp-3 break-words">
                          {c.render
                            ? c.render(row)
                            : c.key === 'created_at' && row[c.key]
                            ? new Date(row[c.key]).toLocaleString()
                            : row[c.key] === 0
                            ? '0'
                            : row[c.key] || '—'}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
